import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  Button,
  EmptyIndicator,
  LoadingIndicator,
  ModalButtonWithForm
} from 'components/common';
import { Select } from 'components/common/form';
import { PathPicture } from 'components/paths';
import { useHomePageSettingsContext } from '.';

import type { ModuleInSettings } from './HomePageSettingsModules';
import type { GetPathsMinimalRes } from 'shared-types/responses';

type Props = {
  module: ModuleInSettings;
  url: string;
} & (
  | { includeActions?: false; updateModule?: never }
  | { includeActions: true; updateModule: (module: ModuleInSettings) => void }
);

const HomePageSettingsTilesPaths = ({
  includeActions = false,
  module,
  updateModule,
  url
}: Props) => {
  const { messages, theme } = useReduxState();

  const { pathOptions } = useHomePageSettingsContext();

  const { data, loading } = useApiData<GetPathsMinimalRes>({
    url: url + '&onlyPathData=true',
    onlyIf: Boolean(url)
  });

  const copyPaths = () => [...module.paths];

  const handleAddPath = ({ pathId }: { pathId: number }) => {
    if (!pathId) return;

    const modifiedModule = {
      ...module,
      limit: (module.paths?.length || 0) + 1,
      paths: [...(module.paths || []), pathId]
    };
    setTimeout(() => updateModule(modifiedModule));
  };

  const handleMovePath = (pathIndex: number, increment: number) => {
    const paths = copyPaths();
    const movedPath = paths.splice(pathIndex, 1)[0];
    paths.splice(pathIndex + increment, 0, movedPath);
    updateModule({ ...module, paths });
  };

  const handleRemovePath = (pathIndex: number) => {
    const paths = copyPaths();
    paths.splice(pathIndex, 1);
    updateModule({ ...module, paths, limit: paths.length });
  };

  return (
    <div>
      {Boolean(url) && loading ? (
        <LoadingIndicator />
      ) : data?.paths?.length || includeActions ? (
        <div>
          <div className="paths">
            {data?.paths?.map((path, i: number) => (
              <div className="path" key={path.id} tabIndex={0}>
                <div className="picture">
                  <PathPicture
                    picture={path.picture}
                    picturePosition={path.picturePosition}
                    size={640}
                  />
                </div>

                <div className="content">
                  <div className="title">{path.title}</div>

                  {includeActions ? (
                    <div className="content-actions">
                      <Button
                        category="lighter"
                        disabled={i === 0}
                        icon="arrow-left"
                        isRounded={true}
                        onClick={() => handleMovePath(i, -1)}
                        size="small"
                        tooltip={messages.commonMove}
                      />
                      <Button
                        category="lighter"
                        disabled={i === module.paths.length - 1}
                        icon="arrow-right"
                        isRounded={true}
                        onClick={() => handleMovePath(i, 1)}
                        size="small"
                        tooltip={messages.commonMove}
                      />
                      <Button
                        category="lighter"
                        icon="trash-alt"
                        isRounded={true}
                        onClick={() => handleRemovePath(i)}
                        size="small"
                        tooltip={messages.commonRemove}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ))}

            {includeActions ? (
              <div className="add">
                <ModalButtonWithForm
                  buttonCategory="transparent"
                  buttonIcon="plus"
                  buttonStyle={{
                    alignItems: 'center',
                    border: `2px dashed ${theme.lightGreyDarkActive}`,
                    borderRadius: theme.cardBorderRadius,
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    width: '100%'
                  }}
                  buttonText={messages.commonAddPath}
                  modalActionCategory="success"
                  modalActionText={messages.commonAdd}
                  modalTitle={messages.commonAddPath}
                  onSubmit={handleAddPath}
                  preventSubmit={true}
                  pristineIsValid={false}
                >
                  {() => (
                    <Select
                      field="pathId"
                      isSearchable={true}
                      noOptionsMessage={messages.pathNotFound}
                      options={pathOptions.filter(
                        (path) => !(module.paths || []).includes(path.value)
                      )}
                      placeholder={messages.pathsSearch}
                    />
                  )}
                </ModalButtonWithForm>
              </div>
            ) : null}
          </div>

          <div className="disclaimer">
            {`* ${messages.homePageModulePathsDisclaimer}`}
          </div>
        </div>
      ) : (
        <EmptyIndicator
          icon="list"
          iconIsDarker={true}
          size="small"
          title={messages.pathsEmpty}
        />
      )}

      <style jsx>{`
        .paths {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 20px;
          padding: 20px;
        }
        .add,
        .path {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          height: 180px;
          outline: 0;
          transition: box-shadow 0.3s;
          width: 100%;
          min-width: 100px;
        }
        .add :global(> button) {
          background-color: ${theme.lightGreyLighter};
          color: ${theme.textColorLighter};
        }
        .add :global(> button:hover) {
          background-color: white;
          color: ${theme.textColor};
        }
        .picture {
          border-radius: ${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0;
          height: 70px;
          overflow: hidden;
        }
        .content {
          color: ${theme.textColor};
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          font-size: 1.5rem;
          font-weight: 600;
          height: 110px;
          justify-content: space-between;
          padding: 10px 15px;
        }
        .title {
          display: flex;
          gap: 10px;
          flex-direction: column;
        }
        .content-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .disclaimer {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
          margin: 0 20px 20px;
        }
        @media (min-width: ${breakpoints.large}px) {
          .add,
          .path {
            width: calc(50% - 10px);
          }
        }
      `}</style>
    </div>
  );
};

export default HomePageSettingsTilesPaths;
